/**
 * مانده اول دوره — شروعِ حسابِ هر طرف در ابتدای سال.
 *
 * چرا لازم است: حسابِ زنجیره هیچ‌وقت بسته نمی‌شود و پارت‌به‌پارت تسویه می‌شود.
 * اگر موجودیِ ابتدای سالِ یک طرف ثبت نشود، ماندهٔ او منفی می‌افتد و به‌غلط
 * شبیهِ «مغایرت» می‌شود — در حالی که فقط شروعِ حساب ثبت نشده است.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, InputNumber, Button, Select, Input, Space, Tag, message, Popconfirm, Row, Col, Statistic, Typography } from "antd";
import Alert from "../../components/GxAlert";
import { SaveOutlined, ReloadOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  scOpeningBalances, scSetOpeningBalance, scDeleteOpeningBalance,
} from "../../api/client";
import { PARTY_TYPE_LABEL } from "./scConstants";
import { useAuth } from "../../context/AuthContext";
import { faNum } from "../../utils/faNumberFormat";

const { Text } = Typography;

const kg = (v) =>
  v == null ? "—" : `${faNum(Number(v), { maximumFractionDigits: 1 })} kg`;

const currentJalaliYear = () => {
  try {
    return Number(new Intl.DateTimeFormat("en-u-ca-persian", { year: "numeric" })
      .format(new Date()).replace(/\D/g, ""));
  } catch {
    return 1405;
  }
};

export default function OpeningBalancePage() {
  const { can } = useAuth();
  const canEdit = can("supply_chain.create");

  const [year, setYear] = useState(currentJalaliYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({});   // party_id → {quantity_kg, item_type_id, notes}

  const load = useCallback(() => {
    setLoading(true);
    scOpeningBalances(year)
      .then((r) => { setData(r.data); setDraft({}); })
      .catch(() => message.error("خطا در دریافت مانده اول دوره"))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const valueOf = (row, field) =>
    draft[row.party_id]?.[field] !== undefined ? draft[row.party_id][field] : row[field];

  const setField = (partyId, field, value) =>
    setDraft((d) => ({ ...d, [partyId]: { ...(d[partyId] || {}), [field]: value } }));

  const save = (row) => {
    const qty = valueOf(row, "quantity_kg");
    if (qty == null || qty === "") {
      message.warning("مقدار را وارد کنید");
      return;
    }
    setLoading(true);
    scSetOpeningBalance({
      party_id: row.party_id,
      quantity_kg: Number(qty),
      item_type_id: valueOf(row, "item_type_id"),
      notes: valueOf(row, "notes") || undefined,
    }, year)
      .then((r) => { message.success(r.data.message); load(); })
      .catch((e) => {
        message.error(e?.response?.data?.detail || "ثبت ناموفق بود");
        setLoading(false);
      });
  };

  const remove = (row) => {
    setLoading(true);
    scDeleteOpeningBalance(row.party_id, year)
      .then(() => { message.success("حذف شد"); load(); })
      .catch((e) => {
        message.error(e?.response?.data?.detail || "حذف ناموفق بود");
        setLoading(false);
      });
  };

  const itemOptions = (data?.item_types || []).map((i) => ({ value: i.id, label: i.name }));

  const columns = [
    { title: "طرف", dataIndex: "party_name", width: 170,
      render: (v, r) => (
        <Space size={4}>
          <b>{v}</b>
          {!r.has_opening && <Tag color="orange">ثبت نشده</Tag>}
        </Space>
      ) },
    { title: "نوع", dataIndex: "party_type", width: 120,
      render: (v) => PARTY_TYPE_LABEL[v] || v },
    { title: "کالا", dataIndex: "item_type_id", width: 160,
      render: (_, r) => (
        <Select size="small" style={{ width: "100%" }} options={itemOptions}
          value={valueOf(r, "item_type_id")} disabled={!canEdit}
          onChange={(v) => setField(r.party_id, "item_type_id", v)} />
      ) },
    { title: "مانده اول دوره (kg)", dataIndex: "quantity_kg", width: 180, align: "left",
      render: (_, r) => (
        <InputNumber size="small" style={{ width: "100%" }} min={0} disabled={!canEdit}
          value={valueOf(r, "quantity_kg")} placeholder="۰"
          onChange={(v) => setField(r.party_id, "quantity_kg", v)} />
      ) },
    { title: "توضیح", dataIndex: "notes",
      render: (_, r) => (
        <Input size="small" disabled={!canEdit} value={valueOf(r, "notes") || ""}
          placeholder="اختیاری"
          onChange={(e) => setField(r.party_id, "notes", e.target.value)} />
      ) },
    { title: "ماندهٔ فعلی", dataIndex: "current_balance_kg", width: 140, align: "left",
      render: (v) => (
        <b style={{ color: v < 0 ? "#cf1322" : "#389e0d" }}>{kg(v)}</b>
      ) },
    { title: "سند", dataIndex: "event_code", width: 140,
      render: (v) => v ? <Text code style={{ fontSize: 11 }}>{v}</Text>
                       : <Text type="secondary">—</Text> },
    ...(canEdit ? [{
      title: "عملیات", width: 150, align: "center",
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" type="primary" icon={<SaveOutlined />}
            onClick={() => save(r)}>ثبت</Button>
          {r.has_opening && (
            <Popconfirm title="سندِ مانده اول دوره حذف شود؟" okText="حذف" cancelText="انصراف"
              onConfirm={() => remove(r)}>
              <Button size="small" danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  const negatives = (data?.rows || []).filter((r) => (r.current_balance_kg ?? 0) < 0);

  return (
    <Card
      title="مانده اول دوره — موجودیِ شروعِ سالِ هر طرف"
      extra={
        <Space>
          <Select value={year} onChange={setYear} style={{ width: 110 }}
            options={[year + 1, year, year - 1, year - 2].map((y) => ({ value: y, label: String(y) }))} />
          <Button icon={<ReloadOutlined />} onClick={load}>بازخوانی</Button>
        </Space>
      }
    >
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="این عدد، حسابِ سالِ هر طرف را از آن نقطه شروع می‌کند"
        description={`برای هر طرف یک سندِ «مانده اول دوره» به تاریخ ${data?.start_date || "ابتدای سال"} ثبت می‌شود. `
          + "مقدار می‌تواند صفر باشد. ثبتِ دوباره همان سند را به‌روز می‌کند و هرگز دوباره‌شماری نمی‌شود."} />

      {data && (
        <Row gutter={12} style={{ marginBottom: 12 }} key={`${year}-${data.missing}`}>
          <Col xs={12} md={8}>
            <Card size="small"><Statistic title="طرف‌ها" value={data.rows.length} /></Card>
          </Col>
          <Col xs={12} md={8}>
            <Card size="small"><Statistic title="بدون مانده اول دوره" value={data.missing}
              valueStyle={{ color: data.missing ? "#cf1322" : "#389e0d" }} /></Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small"><Statistic title="ماندهٔ منفی" value={negatives.length}
              valueStyle={{ color: negatives.length ? "#cf1322" : "#389e0d" }} /></Card>
          </Col>
        </Row>
      )}

      {negatives.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message={`${negatives.length} طرف ماندهٔ منفی دارند`}
          description={"خروجی بیشتر از ورودیِ ثبت‌شده است — معمولاً یعنی موجودیِ ابتدای سالشان ثبت نشده: "
            + negatives.map((r) => `${r.party_name} (${kg(r.current_balance_kg)})`).join("، ")} />
      )}

      <Table size="small" rowKey="party_id" columns={columns} loading={loading}
        dataSource={data?.rows || []} pagination={false} scroll={{ x: "max-content" }} />
    </Card>
  );
}
